import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffOpportunityAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Briefcase, MapPin, Clock,
  IndianRupee, Loader2, CheckCircle, Send, X
} from 'lucide-react';

export default function BrowseOpportunities() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyLoading, setApplyLoading] = useState(null);
  const [showApply, setShowApply] = useState(null);
  const [coverNote, setCoverNote] = useState('');
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => { load(); }, [page]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await staffOpportunityAPI.browse(page, 10);
      const data = res.data.data;
      setOpportunities(data.content || []);
      setTotalPages(data.totalPages || 0);
    } catch { toast.error('Failed to load opportunities'); }
    finally { setLoading(false); }
  };

  const handleApply = async () => {
    if (!showApply) return;
    setApplyLoading(showApply);
    try {
      await staffOpportunityAPI.apply({ opportunityId: showApply, coverNote: coverNote || null });
      toast.success('Application submitted!');
      setShowApply(null);
      setCoverNote('');
      setOpportunities(prev => prev.map(o =>
        o.id === showApply
          ? { ...o, alreadyApplied: true, myApplicationStatus: 'APPLIED' }
          : o
      ));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to apply'); }
    finally { setApplyLoading(null); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded-lg text-xs font-bold uppercase">Staff</span>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">Open Opportunities</h1>
          <p className="text-sm text-gray-400 mt-0.5">Browse and apply to available positions</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Briefcase className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No open opportunities right now</p>
            <p className="text-sm text-gray-400 mt-1">Check back later for new postings</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {opportunities.map(opp => (
                <div key={opp.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-bold text-slate-800">{opp.title}</h3>
                        {opp.alreadyApplied && (
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold
                            ${opp.myApplicationStatus === 'SELECTED'
                              ? 'bg-green-100 text-green-700'
                              : opp.myApplicationStatus === 'REJECTED'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-blue-100 text-blue-700'}`}>
                            {opp.myApplicationStatus === 'SHORTLISTED' ? '⭐ Shortlisted'
                              : opp.myApplicationStatus === 'SELECTED' ? '✅ Selected'
                              : opp.myApplicationStatus === 'REJECTED' ? 'Not selected'
                              : 'Applied'}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-teal-600">{opp.roleName}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-sm text-gray-500">{opp.companyName}</span>
                        <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">
                          {opp.organizationType}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                        {opp.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{opp.city}{opp.state ? `, ${opp.state}` : ''}</span>}
                        {opp.shiftHours && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{opp.shiftHours}hr shift</span>}
                        {(opp.dailyRateMin || opp.dailyRateMax) && (
                          <span className="flex items-center gap-1 font-semibold text-teal-600">
                            <IndianRupee className="h-3 w-3" />
                            {opp.dailyRateMin && `₹${opp.dailyRateMin}`}
                            {opp.dailyRateMin && opp.dailyRateMax && ' – '}
                            {opp.dailyRateMax && `₹${opp.dailyRateMax}`}/day
                          </span>
                        )}
                        {opp.experienceRequired > 0 && <span>{opp.experienceRequired}+ yrs exp</span>}
                        {opp.qualificationRequired && <span>{opp.qualificationRequired}</span>}
                      </div>
                      {opp.description && (
                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">{opp.description}</p>
                      )}
                      {opp.requiredFrom && (
                        <p className="text-xs text-gray-400 mt-1.5">
                          {new Date(opp.requiredFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          {opp.requiredUntil && ` – ${new Date(opp.requiredUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                        </p>
                      )}
                    </div>

                    <div className="shrink-0">
                      {!opp.alreadyApplied ? (
                        <button onClick={() => { setShowApply(opp.id); setCoverNote(''); }}
                          className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-2.5
                            rounded-xl text-sm font-bold hover:bg-teal-700 transition shadow-sm">
                          <Send className="h-3.5 w-3.5" /> Apply
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
                          <CheckCircle className="h-4 w-4" /> Applied
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                <button onClick={() => setPage(p => p - 1)} disabled={page === 0}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-gray-50">
                  Previous
                </button>
                <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-gray-50">
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Apply modal */}
      {showApply && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">Apply for this position</h3>
              <button onClick={() => setShowApply(null)}
                className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Cover Note <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea value={coverNote} onChange={e => setCoverNote(e.target.value)}
                placeholder="Briefly explain why you're a good fit for this role..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowApply(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleApply} disabled={applyLoading === showApply}
                className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 disabled:opacity-60">
                {applyLoading === showApply ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
